import React, { useState } from 'react';
import './Roles.css';
import SearchableSelect from '../../components/shared/SearchableSelect';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import {
  BsPersonFill,
  BsEnvelopeFill,
  BsTelephoneFill,
  BsSearch,
} from 'react-icons/bs';
import {
  validateEmail,
} from '../../utils/validation';
import { showToast } from '../../store/toastStore';
import { PERMISSION_GROUPS } from './constants/constants';
import { ROLE_OPTIONS } from './constants/constants';

const Roles: React.FC = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Support Agent',
    permissions: [] as string[],
  });

  const [search, setSearch] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const togglePermission = (id: string) => {
    setForm((prev) => {
      const exists = prev.permissions.includes(id);
      if (exists) {
        return { ...prev, permissions: prev.permissions.filter((p) => p !== id) };
      }
      return { ...prev, permissions: [...prev.permissions, id] };
    });
  };

  const handleSelectAllCategory = (category: string, checked: boolean) => {
    const group = PERMISSION_GROUPS.find((g) => g.category === category);
    if (!group) return;

    const categoryIds = group.permissions.map((p) => p.id);

    setForm((prev) => {
      let nextPermissions;
      if (checked) {
        nextPermissions = [...new Set([...prev.permissions, ...categoryIds])];
      } else {
        nextPermissions = prev.permissions.filter((id) => !categoryIds.includes(id));
      }
      return { ...prev, permissions: nextPermissions };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = 'Full name is required';
    const emailError = validateEmail(form.email, { required: true });
    if (emailError) nextErrors.email = emailError;
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    showToast('Role configuration saved successfully!', 'success');
  };

  return (
    <div className="roles-page">
      <div className="dh-page-header">
        <div>
          <h1 className="dh-page-title">Roles &amp; Permissions</h1>
          <p className="dh-page-sub">Configure user roles and granular access control for the platform.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="roles-container">
        <div className="roles-section-card">
          <div className="roles-section-header">
            <h3>User Identity &amp; Role</h3>
          </div>

          <div className="roles-form-grid">
            <div className="roles-field-group">
              <label>Full Name</label>
              <div className="relative">
                <BsPersonFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={form.name}
                  onChange={handleInputChange}
                  className={cn('pl-9', fieldErrors.name && 'border-destructive')}
                  required
                />
              </div>
              {fieldErrors.name && <p className="roles-field-err">{fieldErrors.name}</p>}
            </div>

            <div className="roles-field-group">
              <label>Email Address</label>
              <div className="relative">
                <BsEnvelopeFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  value={form.email}
                  onChange={handleInputChange}
                  className={cn('pl-9', fieldErrors.email && 'border-destructive')}
                  required
                />
              </div>
              {fieldErrors.email && <p className="roles-field-err">{fieldErrors.email}</p>}
            </div>

            <div className="roles-field-group">
              <label>Phone Number</label>
              <div className="relative">
                <BsTelephoneFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="tel"
                  name="phone"
                  placeholder="8012345678"
                  value={form.phone}
                  onChange={handleInputChange}
                  className={cn('pl-9', fieldErrors.phone && 'border-destructive')}
                />
              </div>
              {fieldErrors.phone && <p className="roles-field-err">{fieldErrors.phone}</p>}
            </div>

            <div className="roles-field-group">
              <label>Primary Role</label>
              <SearchableSelect
                className="h-9"
                value={form.role}
                options={ROLE_OPTIONS}
                onChange={(value) => setForm((prev) => ({ ...prev, role: value }))}
                placeholder="Select role"
                searchPlaceholder="Search role…"
                aria-label="Primary role"
              />
            </div>
          </div>
        </div>

        <div className="roles-section-card">
          <div className="roles-section-header">
            <div className="header-left">
              <h3>Permission matrix</h3>
            </div>
            <div className="header-right">
              <div className="dh-table-search">
                <BsSearch className="dh-table-search-icon" aria-hidden />
                <input
                  type="text"
                  className="dh-table-search-input"
                  placeholder="Search permissions..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="permissions-grid-wrapper">
            <table className="permissions-table">
              <thead>
                <tr>
                  <th>Module / Permission</th>
                  <th className="cell-center">Status</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {PERMISSION_GROUPS.map((group) => {
                  const filteredPermissions = group.permissions.filter((p) =>
                    p.name.toLowerCase().includes(search.toLowerCase()) ||
                    p.description.toLowerCase().includes(search.toLowerCase()),
                  );

                  if (filteredPermissions.length === 0) return null;

                  const allCategoryChecked = group.permissions.every((p) => form.permissions.includes(p.id));
                  const someCategoryChecked = group.permissions.some((p) => form.permissions.includes(p.id)) && !allCategoryChecked;

                  return (
                    <React.Fragment key={group.category}>
                      <tr className="category-row">
                        <td colSpan={3}>
                          <div className="category-header">
                            <span className="category-name">{group.category}</span>
                            <label className="checkbox-container">
                              <input
                                type="checkbox"
                                checked={allCategoryChecked}
                                ref={(el) => {
                                  if (el) el.indeterminate = someCategoryChecked;
                                }}
                                onChange={(e) => handleSelectAllCategory(group.category, e.target.checked)}
                              />
                              <span className="checkbox-label">Select All</span>
                            </label>
                          </div>
                        </td>
                      </tr>
                      {filteredPermissions.map((perm) => (
                        <tr key={perm.id} className="permission-row">
                          <td className="perm-name">{perm.name}</td>
                          <td className="cell-center">
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={form.permissions.includes(perm.id)}
                                onChange={() => togglePermission(perm.id)}
                              />
                              <span className="slider round"></span>
                            </label>
                          </td>
                          <td className="perm-desc">{perm.description}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-actions-footer">
          <Button type="button" variant="outline" onClick={() => window.history.back()}>
            Discard Changes
          </Button>
          <Button type="submit">
            Save Role Configuration
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Roles;
